import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useGamification } from '@/context/GamificationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Mail, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { trackEvent } from '@/lib/posthog';

const translations = {
  fr: {
    title: "Dernière étape !",
    subtitle: "Entrez votre email pour télécharger",
    emailPlaceholder: "votre@email.com",
    submit: "Télécharger mon CV →",
    submitting: "Traitement en cours...",
    success: "Document prêt !",
    successMessage: "Vérifiez votre email pour découvrir EdoMatch",
    error: "Une erreur est survenue",
    required: "Ce champ est requis",
    invalidEmail: "Email invalide",
    bonus: "+50 points bonus pour compléter votre profil !",
    privacyNote: "🔒 Vos données sont protégées"
  },
  en: {
    title: "One last step!",
    subtitle: "Enter your email to download",
    emailPlaceholder: "your@email.com",
    submit: "Download my CV →",
    submitting: "Processing...",
    success: "Document ready!",
    successMessage: "Check your email to discover EdoMatch",
    error: "An error occurred",
    required: "This field is required",
    invalidEmail: "Invalid email",
    bonus: "+50 bonus points for completing your profile!",
    privacyNote: "🔒 Your data is protected"
  }
};

export default function DownloadGateModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  profileId, 
  downloadType = 'cv_pdf' 
}) {
  const { language } = useLanguage();
  const { addPoints, unlockBadge } = useGamification();
  const t = translations[language] || translations.fr;
  
  // Form state
  const [email, setEmail] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alreadyCaptured, setAlreadyCaptured] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkLeadStatus();
    }
  }, [isOpen]);

  // Check if lead already captured
  const checkLeadStatus = async () => {
    if (!profileId) return;
    try {
      const res = await api.get(`/leads/status/${profileId}`);
      if (res.data.captured) {
        setAlreadyCaptured(true);
        // Auto-proceed to download since already captured
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Lead status check failed:', error);
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = t.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t.invalidEmail;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      const response = await api.post('/leads/capture', {
        profile_id: profileId,
        email,
        language,
        download_type: downloadType
      });
      
      if (response.data.success) {
        // Award points
        if (response.data.points_awarded > 0) {
          addPoints(response.data.points_awarded);
        }
        
        // Unlock badge if earned
        if (response.data.badge_earned) {
          unlockBadge(response.data.badge_earned);
        }
        
        // Track account creation event
        trackEvent('account_created', {
          source: 'cv_review',
          language
        });
        
        toast.success(t.success, {
          description: response.data.email_sent ? t.successMessage : undefined
        });
        
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Lead capture failed:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  if (alreadyCaptured) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] bg-white dark:bg-slate-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-center">
            <Download className="w-6 h-6 text-[#5BA946]" />
            {t.title}
          </DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-1">{t.subtitle}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Bonus Banner */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <span className="text-sm text-amber-700 dark:text-amber-300 font-medium">{t.bonus}</span>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPlaceholder}
                className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                data-testid="lead-email-input"
                autoFocus
              />
            </div>
            {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {t.privacyNote}
          </p>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#5BA946] hover:bg-[#4a9339] text-white py-6 text-lg"
            data-testid="lead-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t.submitting}
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                {t.submit}
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
